import { useEffect, useCallback, useRef } from 'react';

interface UseAnimatedBorderOptions {
  gradientColors?: {
    from: string;
    to: string;
  };
  animationDuration?: number;
  animationGap?: number;
}

const indexMapClassName = {
  0: 'animate-border-top',
  1: 'animate-border-right',
  2: 'animate-border-bottom',
  3: 'animate-border-left',
};

function convertMsToS(ms: number): string {
  const seconds = ms / 1000;
  return `${seconds}s`;
}

export const useAnimatedBorder = (containerRef: React.RefObject<HTMLElement>, options?: UseAnimatedBorderOptions) => {
  const {
    gradientColors = {
      from: '#ff5b271a',
      to: '#ff5b27',
    },
    animationDuration = 2000,
    animationGap = 10000,
  } = options || {};

  const borderContainerRef = useRef<HTMLDivElement | null>(null);

  const createBorders = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const borderContainer = document.createElement('div');
    borderContainer.style.position = 'absolute';
    borderContainer.style.inset = '0';
    borderContainer.style.pointerEvents = 'none';

    ['top', 'right', 'bottom', 'left'].map((side) => {
      const border = document.createElement('div');
      border.style.position = 'absolute';
      border.style.animationDuration = convertMsToS(animationDuration);

      switch (side) {
        case 'top':
          border.style.top = '0';
          border.style.left = '0';
          border.style.width = '100%';
          border.style.height = '1px';
          border.style.background = `linear-gradient(to right, ${gradientColors.from}, ${gradientColors.to},${gradientColors.from})`;
          border.classList.add('animate-border-top');
          break;
        case 'right':
          border.style.top = '0';
          border.style.right = '0';
          border.style.height = '100%';
          border.style.width = '1px';
          border.style.background = `linear-gradient(to bottom,${gradientColors.from}, ${gradientColors.to},${gradientColors.from})`;
          border.classList.add('animate-border-right');
          break;
        case 'bottom':
          border.style.bottom = '0';
          border.style.left = '0';
          border.style.width = '100%';
          border.style.height = '1px';
          border.style.background = `linear-gradient(to left, ${gradientColors.from}, ${gradientColors.to},${gradientColors.from})`;
          border.classList.add('animate-border-bottom');
          break;
        case 'left':
          border.style.top = '0';
          border.style.left = '0';
          border.style.height = '100%';
          border.style.width = '1px';
          border.style.background = `linear-gradient(to top, ${gradientColors.from}, ${gradientColors.to},${gradientColors.from})`;
          border.classList.add('animate-border-left');
          break;
      }

      borderContainer.appendChild(border);
      return border;
    });

    return borderContainer;
  }, [gradientColors]);

  const handleAnimationEnd = () => {
    stop();
    setTimeout(() => {
      start();
    }, animationGap);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.style.overflow = 'hidden';

    const borderContainer = createBorders();
    if (!borderContainer) return;
    container.appendChild(borderContainer);
    borderContainerRef.current = borderContainer;

    const borderTop = borderContainerRef.current?.childNodes[0];
    if (borderTop instanceof HTMLElement) {
      borderTop.addEventListener('animationend', handleAnimationEnd);
    }
  }, []);

  const start = useCallback(() => {
    if (borderContainerRef.current) {
      borderContainerRef.current.style.opacity = '1';
    }
    borderContainerRef.current?.childNodes?.forEach((borderNode, index) => {
      const className = indexMapClassName[index as 0 | 1 | 2 | 3];
      if (borderNode instanceof HTMLElement) {
        if (!borderNode.classList.contains(className)) {
          borderNode.classList.add(className);
        }
      }
    });
  }, [createBorders]);

  const stop = useCallback(() => {
    if (borderContainerRef.current) {
      borderContainerRef.current.style.opacity = '0';
    }

    borderContainerRef.current?.childNodes?.forEach((borderNode, index) => {
      const className = indexMapClassName[index as 0 | 1 | 2 | 3];
      if (borderNode instanceof HTMLElement) {
        borderNode.classList.remove(className);
      }
    });
  }, []);

  const clear = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const borderContainer = borderContainerRef.current;

    if (borderContainer) {
      container.removeChild(borderContainer);
    }
  }, [handleAnimationEnd]);

  return { start, stop, clear };
};
